import React, {useEffect, useState} from "react";
import AdvertCard from "./components/AdvertCard";
import Dropdown from "./components/Dropdown";
import SortIcon from "./assets/icons/SortIcon";
import ReactPaginate from "react-paginate";
import useNoInitialEffect from "./hooks/useNoInitialRender";
import {SORT_BY_FIELDS} from "./data/defaults";
import useMediaQuery from "./hooks/useMediaQuery";
import {useLocation, useSearchParams} from "react-router-dom";
import {formatNumber} from "./utils/numberFormat";
import {SearchBox} from "./components/SearchBox";
import Modal from "./components/Modal";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootStore} from "./store/rootStore";
import {
    initAdsFromCache,
    initStateFromQueryParams,
    setCachedResponse,
    setCurrentPage,
    setFilters,
    setPageSize,
    setSortField
} from "./store/reducers/searchResultReducer";
import {getParams} from "./store/selectors";
import {fetchAds} from "./store/actions/fetchAds";
import {RequestParams} from "./api/RequestParams";
import FilterEditIcon from "./assets/icons/FilterEditIcon";

if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
}

let browserHistoryPopped = false;

const SearchResultApp: React.FC = () => {
    const {
        adverts, cachedResponse, filters, currentPage, totalPages, pageSize, resultCount, sortField, adsLoading
    } = useSelector((store: RootStore) => {
        return {
            adverts: store.searchResult.cachedResponse?.data ?? [],
            filters: store.searchResult.filters,
            cachedResponse: store.searchResult.cachedResponse,
            currentPage: store.searchResult.currentPage,
            totalPages: store.searchResult.totalPages,
            pageSize: store.searchResult.pageSize,
            sortField: store.searchResult.sortField,
            resultCount: store.searchResult.resultCount,
            adsLoading: store.searchResult.adsLoading,
        }
    });
    const params = useSelector(getParams);
    const dispatch = useDispatch<AppDispatch>();

    let routerLocation = useLocation();
    let [_, setSearchParams] = useSearchParams({});

    const isMobile = useMediaQuery("(max-width: 800px)");
    const [initialLoading, setInitialLoading] = useState(true);
    const [modalShown, setModalShown] = useState(false);

    const getParamsFromUrl = () => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());

        return new RequestParams({
                make: params.make,
                model: params.model,
                colour: params.colour,
                "body-style": params["body-style"],
                "fuel-type": params["fuel-type"],
                price: params.price ? +params.price : undefined,
                doors: params.doors ? +params.doors : undefined,
                transmission: params.transmission,
                "geo.location": params["geo.location"],
                "geo.miles": params["geo.miles"] ? +params["geo.miles"] : undefined,
                "geo.postcode": params["geo.postcode"],
            },
            {
                field: params.sort ? params.sort : "latest-cars",
                direction: SORT_BY_FIELDS.find(i => i.id == (params.sort ? params.sort : "price"))?.desc ? "desc" : "asc"
            },
            {
                size: params.size ? +params.size : 8,
                number: params.number ? +params.number : 1
            }
        );
    }

    async function loadAdverts() {
        if (cachedResponse && initialLoading) {
            await dispatch(initAdsFromCache());
            localStorage.removeItem("tmp-search-result");
        } else {
            await dispatch(fetchAds());
        }
        (window as any)?.syncAdvertCardsWithWishlist();
        setInitialLoading(false);
    }

    useEffect(() => {
        void loadAdverts();
    }, [routerLocation]);

    useEffect(() => {
        async function popHistoryHandler(e: PopStateEvent) {
            if (e.state !== null) {
                browserHistoryPopped = true;
                await dispatch(initStateFromQueryParams(getParamsFromUrl()));
                setTimeout(() => (browserHistoryPopped = false), 100)
            }
        }

        window.addEventListener("popstate", popHistoryHandler);
        return () => window.removeEventListener("popstate", popHistoryHandler);
    }, []);

    useNoInitialEffect(() => {
        if (!browserHistoryPopped) {
            setSearchParams(params.toString(), {
                replace: false,
            });
        }
    }, [filters, currentPage, pageSize, sortField]);

    useNoInitialEffect(() => {
        requestAnimationFrame(() => {
            const registerAdvertSliders = (document as any).registerAdvertSliders;
            registerAdvertSliders && registerAdvertSliders()
        });
    }, [adverts]);

    return (
        <>
            <Modal open={modalShown} closeHandler={() => {
                setModalShown(false);
            }}>
                <div className="modal-search-box-container">
                    <div className="row" id="search-box-modal">
                        <SearchBox
                            expanded={true}
                            onSubmitHandler={async (params, apiResponse) => {
                                setModalShown(false);
                                if (apiResponse) {
                                    await dispatch(setCachedResponse(apiResponse));
                                    await dispatch(setFilters(params?.filters!))
                                }
                            }}
                        />
                    </div>
                </div>
            </Modal>
            <div className={"search-result"} style={{paddingTop: isMobile ? "1.5rem" : "3rem"}}>
                {adverts.length > 0 &&
                    <div
                        className="container d-flex flex-column flex-md-row align-md-items-center justify-content-between pb-3">
                        <div className="row w-100 gx-0">
                            <div className="col-12 col-md-6 d-flex align-items-center mt-4 mt-md-0"
                                 style={{order: isMobile ? 1 : 0}}>
                                <h3 className="mb-0 search-result-title">
                                    {formatNumber(resultCount)}&nbsp;
                                    {
                                        !filters.make ?
                                            `Car${resultCount > 1 ? "s" : ""}` :
                                            <span>
                                                {filters.make && filters.make} {filters.model && filters.model}
                                            </span>
                                    } for Sale
                                </h3>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="row gx-0 w-100 justify-content-md-end">
                                    <div className="col-6 col-md-5 d-flex justify-content-md-end"
                                         style={{paddingRight: "1rem"}}>
                                        <button className="refine-search-btn" onClick={() => {
                                            setModalShown(true);
                                        }}>
                                            <FilterEditIcon/>
                                            <span>Edit Filters</span>
                                        </button>
                                    </div>
                                    <div className="col-6 col-md-7">
                                        <div className="form-group w-100 d-flex align-items-center"
                                             style={{gap: ".5rem"}}>
                                            <Dropdown
                                                name="sortDropdown"
                                                value={sortField}
                                                label={!isMobile ? "Sort by" : ""}
                                                labelStyles={{fontSize: "14px", color: "#6d7a80"}}
                                                data={SORT_BY_FIELDS}
                                                icon={<SortIcon/>}
                                                valueFormatter={(value) => {
                                                    return `${value}`;
                                                }}
                                                onChange={(value) => {
                                                    dispatch(setSortField(value));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>}

                <div className={["search-result-list py-3 py-md-4", adverts.length < 1 ? "no-result" : ""].join(" ")}
                     style={{position: "relative", minHeight: adsLoading ? (isMobile ? "500px" : "700px") : "auto"}}>
                    {(adsLoading && !initialLoading) && <div className="loader-overlay">
                        <span className="loader"></span>
                    </div>}

                    <div className="container">
                        <div>
                            {initialLoading && <div>
                                <div className="loader-overlay" style={{position: "fixed"}}>
                                    <span className="loader"></span>
                                </div>
                            </div>}
                            <div className="row gx-3 gy-3 gy-md-4">
                                {adverts.map(ad => (
                                        <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={ad.id}>
                                            <div className="search-result-item">
                                                <AdvertCard advert={ad}/>
                                            </div>
                                        </div>
                                    )
                                )}
                                {!adsLoading && adverts.length < 1 &&
                                    <div
                                        className="d-flex flex-column align-items-center justify-content-center py-5 mb-5"
                                        style={{margin: "0 auto"}}>
                                        <svg width="129" height="129" viewBox="0 0 129 129" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M112.686 54.0037L103.836 28.7197C101.424 21.8311 94.8967 17.2 87.5953 17.2H41.4047C34.1033 17.2 27.5759 21.8311 25.1679 28.7154L16.3185 53.9994C11.5928 57.1083 8.60001 62.479 8.60001 68.3872V98.9C8.60001 103.652 12.4485 107.5 17.2 107.5C21.9515 107.5 25.8 103.652 25.8 98.9V93.4777C25.8 93.4777 48.5083 94.6 64.5 94.6C80.4917 94.6 103.2 93.4777 103.2 93.4777V98.9C103.2 103.652 107.049 107.5 111.8 107.5C116.552 107.5 120.4 103.652 120.4 98.9V68.3872C120.4 62.479 117.403 57.1083 112.686 54.0037ZM28.3585 45.6359L33.2863 31.5577C34.4946 28.1091 37.7497 25.8 41.4047 25.8H87.5953C91.2503 25.8 94.5054 28.1091 95.7137 31.5577L100.642 45.6359C101.416 47.8504 99.5708 50.0821 97.2531 49.708C88.8208 48.3449 77.9805 47.3 64.5 47.3C51.0195 47.3 40.1792 48.3449 31.7469 49.708C29.4292 50.0821 27.5845 47.8504 28.3585 45.6359ZM27.95 77.4C24.3896 77.4 21.5 74.5104 21.5 70.95C21.5 67.3896 24.3896 64.5 27.95 64.5C31.5104 64.5 34.4 67.3896 34.4 70.95C34.4 74.5104 31.5104 77.4 27.95 77.4ZM77.4 73.1H51.6C49.2264 73.1 47.3 71.1736 47.3 68.8C47.3 66.4264 49.2264 64.5 51.6 64.5H77.4C79.7736 64.5 81.7 66.4264 81.7 68.8C81.7 71.1736 79.7736 73.1 77.4 73.1ZM101.05 77.4C97.4896 77.4 94.6 74.5104 94.6 70.95C94.6 67.3896 97.4896 64.5 101.05 64.5C104.61 64.5 107.5 67.3896 107.5 70.95C107.5 74.5104 104.61 77.4 101.05 77.4Z"
                                                fill="#3BC9E7"/>
                                        </svg>
                                        <h2 style={{fontWeight: "600"}}>No Results Found</h2>
                                        <p style={{fontSize: "18px", textAlign: "center"}}>Unfortunately we don't have
                                            any
                                            vehicles that match your
                                            search criteria, please
                                            change
                                            the search filters and try again.</p>
                                    </div>}
                            </div>
                        </div>

                        <div
                            style={{display: !initialLoading && adverts.length > 0 ? "flex" : "none"}}
                            className="flex-column flex-md-row mt-4 pt-md-2 mb-3 align-items-md-center justify-content-between ads-pagination w-100"
                        >
                            <div style={{flex: 3}}>
                                <ReactPaginate
                                    breakLabel={"..."}
                                    nextLabel="»"
                                    previousLabel="«"
                                    onPageChange={(e) => {
                                        dispatch(setCurrentPage(e.selected + 1));
                                        window.scrollTo({top: 0, behavior: "smooth"});
                                    }}
                                    pageRangeDisplayed={isMobile ? 4 : 5}
                                    marginPagesDisplayed={isMobile ? 0 : 1}
                                    pageCount={totalPages}
                                    forcePage={currentPage - 1}
                                    renderOnZeroPageCount={null}
                                />
                            </div>

                            <div className="row" style={{flex: 1}}>
                                <div className="col-8 col-md-12 mt-4 mt-md-0">
                                    <div className="page-size-dropdown form-group w-100 d-flex align-items-center"
                                         style={{gap: ".5rem"}}>
                                        <Dropdown
                                            name="pageSizeDropdown"
                                            label={"Per page"}
                                            data={[8, 12, 16, 20, 28, 36]}
                                            value={pageSize}
                                            valueFormatter={(value) => {
                                                return `${value} cars`;
                                            }}
                                            onChange={(value) => {
                                                dispatch(setPageSize(+value));
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchResultApp;
